import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import { useApolloClient } from '@apollo/client'

import { AssociateDeviceI } from '@/features/floor-plan'
import { VideoDimensionsIProps } from '@/features/video-player'
import {
  useDeleteTailgateGridMutation,
  useUpdateTailgateGridMutation,
} from '@/graphql/generated/hooks'
import {
  DeleteTailgateGridInput,
  UpdateCameraInput,
  UpdateTailgateGridInput,
} from '@/graphql/generated/schemas'
import { includeIfActive } from '@/utils/apollo'

import { TailgateBox } from '../types/types'
import { getBoxInput } from '../utils/utils'

interface UseUpdateTailgateGridIProps {
  associatedDevices: AssociateDeviceI | null
  onClose: () => void
  onSaveCallback?: () => void
}

export const useUpdateTailgateGrid = ({
  associatedDevices,
  onClose,
  onSaveCallback,
}: UseUpdateTailgateGridIProps) => {
  const [isLoading, setLoading] = useState(false)
  const client = useApolloClient()

  const { showSuccess, showError } = useToasts({ toastDuration: 3000 })

  const [updateTailgateGrid] = useUpdateTailgateGridMutation()
  const [deleteTailgateGrid] = useDeleteTailgateGridMutation()

  const updateGrid = async (
    inBox: TailgateBox,
    outBox: TailgateBox,
    videoDimensions: VideoDimensionsIProps
  ) => {
    if (!associatedDevices) return
    const { cameraId, doorId, cameraName, doorName } = associatedDevices

    setLoading(true)

    const updateTailgateGridInput: UpdateTailgateGridInput = {
      cameraId,
      doorId,
      inBox: getBoxInput(inBox),
      outBox: getBoxInput(outBox),
    }
    // Camera streaming resolution should be updated on each tailgating grid update
    const updateCameraInput: UpdateCameraInput = {
      id: cameraId,
      streamingResolutionHeight: videoDimensions.height,
      streamingResolutionWidth: videoDimensions.width,
    }

    try {
      await updateTailgateGrid({
        variables: { updateTailgateGridInput, updateCameraInput },
        // AssociatedDevices drives the association-status tag in the device
        // drawer tab (Grids Drawn); includeIfActive keeps the floor-plan
        // path from logging Apollo's unknown-query warning where the tab
        // isn't mounted.
        refetchQueries: [
          'FacilityDevicesByFloors',
          'TailgateGrid',
          ...includeIfActive(client, 'AssociatedDevices'),
        ],
      })
      showSuccess(
        `Successfully edited Tailgate Grid for Associated Device ${cameraName} and ${doorName}`
      )
      onClose()
      onSaveCallback?.()
    } catch {
      showError('Error editing Tailgate Grid')
    }
    setLoading(false)
  }

  const deleteGrid = async () => {
    if (!associatedDevices) return
    const { cameraId, doorId, cameraName, doorName } = associatedDevices

    setLoading(true)
    const input: DeleteTailgateGridInput = { cameraId, doorId }
    try {
      await deleteTailgateGrid({
        variables: { input },
        refetchQueries: [
          'FacilityDevicesByFloors',
          'TailgateGrid',
          ...includeIfActive(client, 'AssociatedDevices'),
        ],
      })
      showSuccess(
        `Successfully removed Tailgate Grid for Associated Device ${cameraName} and ${doorName}`
      )
      onClose()
      onSaveCallback?.()
    } catch {
      showError('Error removing Tailgate Grid')
    }
    setLoading(false)
  }

  return {
    isUpdateLoading: isLoading,
    updateGrid,
    deleteGrid,
  }
}
